import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useCamp } from '../contexts/CampContext';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, ExternalLink, Bell, BellOff, BellRing } from 'lucide-react';
import './CampPublicLayout.css';
import { useT } from '../i18n/index';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { usePush } from '../utils/usePush';

// Helper: set or create a <meta> tag by name
function setMetaTag(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function CampPublicLayout() {
  const { camp, loading, error, isModuleEnabled, campSlug } = useCamp();
  const { user } = useAuth();
  const t = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!pushOpen) return;
    const close = (e) => { if (!e.target.closest('.push-bell-wrap')) setPushOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [pushOpen]);
  const { supported, permission, subscribed, loading: pushLoading, requestAndSubscribe, unsubscribe } = usePush();

  // PWA: inject tenant-specific manifest so this camp installs as its own app.
  useEffect(() => {
    if (!campSlug || !camp) return;
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.href = `/${campSlug}/manifest.json`;
    setMetaTag('apple-mobile-web-app-title', camp.name);

    // Dynamically set apple-touch-icon to the camp logo so iOS uses it as PWA icon
    const logoUrl = camp.branding?.logo_url;
    let touchIcon = document.querySelector('link[rel="apple-touch-icon"][data-camp]');
    if (logoUrl) {
      if (!touchIcon) {
        touchIcon = document.createElement('link');
        touchIcon.rel = 'apple-touch-icon';
        touchIcon.setAttribute('data-camp', '1');
        document.head.appendChild(touchIcon);
      }
      touchIcon.href = logoUrl;
    } else if (touchIcon) {
      touchIcon.remove();
    }

    return () => {
      if (link) link.href = '/manifest.json';
      document.querySelector('link[rel="apple-touch-icon"][data-camp]')?.remove();
    };
  }, [campSlug, camp]);

  if (loading) return (
    <div className="loading-screen">
      <div className="spinner" />
      <span>{t('layout.loading')}</span>
    </div>
  );

  if (error) return (
    <div className="camp-error">
      <div className="camp-error-inner">
        <h1>{t('layout.camp_not_found')}</h1>
        <p>{error}</p>
        <Link to="/" className="btn btn-primary">{t('common.to_home')}</Link>
      </div>
    </div>
  );

  const branding = camp?.branding || {};
  const primaryColor = branding.primary_color || 'var(--color-primary)';

  return (
    <div className="camp-public-layout" style={{ '--camp-primary': primaryColor }}>
      <header className="camp-public-header">
        <div className="camp-public-header-inner">
          <Link to={"/" + campSlug} className="camp-logo">
            {branding.logo_url
              ? <img src={branding.logo_url} alt={camp.name} />
              : <span className="camp-logo-text">{camp.name}</span>
            }
          </Link>

          <nav className="camp-public-nav">
            <NavLink to={"/" + campSlug} end className={({ isActive }) => isActive ? 'active' : ''}>{t('pub.home')}</NavLink>
            {isModuleEnabled('news') && <NavLink to={"/" + campSlug + "/news"} className={({ isActive }) => isActive ? 'active' : ''}>{t('pub.news')}</NavLink>}
            {isModuleEnabled('teams') && <NavLink to={"/" + campSlug + "/teams"} className={({ isActive }) => isActive ? 'active' : ''}>{t('pub.teams')}</NavLink>}
            {isModuleEnabled('tournament') && <NavLink to={"/" + campSlug + "/turnier"} className={({ isActive }) => isActive ? 'active' : ''}>{t('pub.tournament')}</NavLink>}
            {isModuleEnabled('schedule') && <NavLink to={"/" + campSlug + "/abende"} className={({ isActive }) => isActive ? 'active' : ''}>{t('pub.evenings')}</NavLink>}
            <NavLink to={"/" + campSlug + "/anmeldung"} className={({ isActive }) => isActive ? 'active' : ''}>{t('pub.registration')}</NavLink>
            {isModuleEnabled('flyer') && <NavLink to={"/" + campSlug + "/downloads"} className={({ isActive }) => isActive ? 'active' : ''}>{t('pub.downloads')}</NavLink>}
          </nav>

          <div className="camp-public-actions">
            <LanguageSwitcher variant="dropdown" />

            {/* Push notification bell — desktop */}
            {supported && permission !== 'denied' && (
              <div className="push-bell-wrap" style={{ position: 'relative' }}>
                <button
                  onClick={() => setPushOpen(v => !v)}
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
                    background: pushOpen ? 'var(--color-surface-2, rgba(255,255,255,0.08))' : 'transparent',
                    border: '1px solid',
                    borderColor: subscribed ? 'rgba(255,214,10,0.35)' : 'transparent',
                    transition: 'all 150ms',
                  }}
                >
                  {subscribed
                    ? <BellRing size={16} style={{ color: 'var(--color-accent)' }} />
                    : <Bell size={16} style={{ color: 'var(--color-muted)' }} />
                  }
                  {!subscribed && (
                    <span style={{
                      position: 'absolute', top: 6, right: 6,
                      width: 6, height: 6, borderRadius: '50%',
                      background: 'var(--camp-primary, #e63946)',
                      border: '1.5px solid var(--color-bg)',
                    }} />
                  )}
                </button>

                {pushOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                    width: 256, zIndex: 300,
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderTop: '2px solid var(--color-accent)',
                    borderRadius: '0 0 10px 10px',
                    boxShadow: '0 16px 48px rgba(0,0,0,.5)',
                    overflow: 'hidden',
                  }}>
                    {subscribed ? (
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            background: 'rgba(255,214,10,0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <BellRing size={15} style={{ color: 'var(--color-accent)' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-text)', fontFamily: 'Oswald, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                              {t('push.active')}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#5be0a0', display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#5be0a0', display: 'inline-block' }} />
                              Live
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={async () => { await unsubscribe(); setPushOpen(false); }}
                          style={{
                            width: '100%', padding: '7px', borderRadius: 6, cursor: 'pointer',
                            background: 'transparent', border: '1px solid var(--color-border)',
                            color: 'var(--color-muted)', fontSize: '0.78rem', fontWeight: 600,
                            transition: 'all 150ms',
                          }}
                          onMouseOver={e => { e.currentTarget.style.borderColor = '#e63946'; e.currentTarget.style.color = '#e63946'; }}
                          onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-muted)'; }}
                        >
                          {t('push.disable')}
                        </button>
                      </div>
                    ) : (
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            background: 'rgba(230,57,70,0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Bell size={15} style={{ color: 'var(--camp-primary, #e63946)' }} />
                          </div>
                          <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-text)', fontFamily: 'Oswald, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                            {t('push.title')}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', marginBottom: 12, lineHeight: 1.55 }}>
                          {t('push.desc')}
                        </div>
                        <button
                          disabled={pushLoading}
                          onClick={async () => { const ok = await requestAndSubscribe(); if (ok) setPushOpen(false); }}
                          style={{
                            width: '100%', padding: '8px', borderRadius: 6, cursor: 'pointer',
                            background: 'var(--camp-primary, #e63946)', border: 'none',
                            color: '#fff', fontSize: '0.78rem', fontWeight: 700,
                            fontFamily: 'Oswald, sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            opacity: pushLoading ? 0.6 : 1,
                          }}
                        >
                          {pushLoading
                            ? <span className="spinner" style={{ width: 12, height: 12 }} />
                            : <><Bell size={12} /> {t('push.enable')}</>
                          }
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {user
              ? <button className="btn btn-accent btn-sm" onClick={() => navigate("/" + campSlug + "/intern/dashboard")}>
                  <ExternalLink size={14} /> {t('layout.intern')}
                </button>
              : <Link to={"/" + campSlug + "/login"} className="btn btn-primary btn-sm">{t('layout.login')}</Link>
            }
            <button className="mobile-menu-btn no-print" onClick={() => setMenuOpen(v => !v)}>
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="camp-mobile-nav" onClick={() => setMenuOpen(false)}>
            <NavLink to={"/" + campSlug} end>{t('pub.home')}</NavLink>
            {isModuleEnabled('news') && <NavLink to={"/" + campSlug + "/news"}>{t('pub.news')}</NavLink>}
            {isModuleEnabled('teams') && <NavLink to={"/" + campSlug + "/teams"}>{t('pub.teams')}</NavLink>}
            {isModuleEnabled('tournament') && <NavLink to={"/" + campSlug + "/turnier"}>{t('pub.tournament')}</NavLink>}
            {isModuleEnabled('schedule') && <NavLink to={"/" + campSlug + "/abende"}>{t('pub.evenings')}</NavLink>}
            <NavLink to={"/" + campSlug + "/anmeldung"}>{t('pub.registration')}</NavLink>
            {isModuleEnabled('flyer') && <NavLink to={"/" + campSlug + "/downloads"}>{t('pub.downloads')}</NavLink>}
            <hr />
            {user
              ? <NavLink to={"/" + campSlug + "/intern/dashboard"}>{t('nav.intern_area')}</NavLink>
              : <NavLink to={"/" + campSlug + "/login"}>{t('layout.login')}</NavLink>
            }
            {supported && permission !== 'denied' && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 0', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {subscribed
                    ? <BellRing size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                    : <Bell size={16} style={{ color: 'var(--color-muted)', flexShrink: 0 }} />
                  }
                  <span style={{ fontSize: '0.9rem', color: subscribed ? 'var(--color-text)' : 'var(--color-muted)' }}>
                    {t('push.enable_prompt')}
                  </span>
                </div>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    subscribed ? unsubscribe() : requestAndSubscribe();
                  }}
                  disabled={pushLoading}
                  style={{
                    flexShrink: 0, padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
                    fontSize: '0.72rem', fontWeight: 700,
                    fontFamily: 'Oswald, sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase',
                    border: '1px solid',
                    background: subscribed ? 'transparent' : 'var(--camp-primary, #e63946)',
                    borderColor: subscribed ? 'var(--color-border)' : 'var(--camp-primary, #e63946)',
                    color: subscribed ? 'var(--color-muted)' : '#fff',
                    opacity: pushLoading ? 0.6 : 1,
                  }}
                >
                  {pushLoading
                    ? '...'
                    : subscribed ? t('push.disable') : t('push.enable')
                  }
                </button>
              </div>
            )}
          </nav>
        )}
      </header>

      <main className="camp-public-main">
        <Outlet />
      </main>

      <footer className="camp-public-footer">
        <div className="camp-public-footer-inner">
          <div className="footer-brand">
            <div className="footer-brand-name">{camp.name}</div>
            {camp.year && <div className="footer-brand-year">{camp.year}</div>}
            {camp.location && <div className="footer-tagline">{camp.location}</div>}
          </div>
          <div className="footer-links">
            {camp.contact_email && <a href={"mailto:" + camp.contact_email}>{camp.contact_email}</a>}
            {camp.branding?.privacy_url && <a href={camp.branding.privacy_url} target="_blank" rel="noopener noreferrer">Datenschutz</a>}
            {camp.branding?.impressum_url && <a href={camp.branding.impressum_url} target="_blank" rel="noopener noreferrer">Impressum</a>}
          </div>
        </div>
        <div className="footer-bottom">© {new Date().getFullYear()} {camp.name}. Alle Rechte vorbehalten.</div>
      </footer>
    </div>
  );
}
